import { Box, Typography, Dialog, UserSocialMediaLinks, SVGIcon, ExplorerPlusIcon, Chip } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useState } from 'react';
import { getTransportMode, getUserMembershipLabel } from 'src/data';
import { useAuth } from 'src/utils';
import { useFeatureTravellerStore } from 'src/stores';

import UserDetailsModalHeader from './user-details-modal-header';
import UserTripList from './user-trips-list';
import UserStaticMapHeader from './user-static-map-header';
import MembershipsModal from 'src/pages/memberships/memberships-modal';

import vehicleBackgroundImage from 'src/assets/statistics-vehicle-bg.png';
import globeBackgroundImage from 'src/assets/placeholders/globe.png';

export default function UserDetailsModal({ onClose, mapFlyTo }) {
    const { user } = useAuth();
    const isMobile = useIsMobile();

    const selectedTraveller = useFeatureTravellerStore((s) => s.selectedTraveller);
    const loadedTravellerData = useFeatureTravellerStore((s) => s.loadedTravellerData);

    const [displayMembershipsModal, setDisplayMembershipsModal] = useState(false);

    if (!selectedTraveller && !loadedTravellerData[selectedTraveller?.id]) return null;

    const fullUser = loadedTravellerData[selectedTraveller?.id] || selectedTraveller;

    const {
        id,
        bio,
        instagramHandle,
        membership,
        socialMediaLinks,
        locationCoords,
        vehicleImageUrl,
        transportMode,
        trips = [],
    } = fullUser;

    const handleFlyTo = () => {
        onClose();
        mapFlyTo?.(locationCoords, 10);
    };

    const handleMembershipTagClick = () => {
        if (user.membership === "Free") {
            setDisplayMembershipsModal(true);
        }
    };

    return (<>
        <Dialog
            open
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                className="scrollBox"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    position: 'relative',
                    overflowY: 'auto',
                }}
            >
                <Box sx={{ flexShrink: 0, height: '35vh' }}>
                    <UserStaticMapHeader
                        coords={locationCoords}
                        onClose={onClose}
                        onFlyTo={handleFlyTo}
                    />
                </Box>

                <Box sx={{ flex: 1, p: 4, mt: -5, bgcolor: '#fff', borderTopRightRadius: 30, borderTopLeftRadius: 30, zIndex: 2 }}>

                    <UserDetailsModalHeader />

                    {/* Bio */}
                    {bio && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {bio}
                            </Typography>
                        </Box>
                    )}

                    {/* Social Media */}
                    <Box sx={{display: 'flex', flexWrap: "wrap", alignItems: 'center', mt: 2, mb: 2, width: '100%'}}>
                        {membership !== "Free" && (
                            <Chip
                                onClick={() => handleMembershipTagClick()}
                                icon={
                                    <ExplorerPlusIcon 
                                        tier={membership.toLowerCase()}
                                        size={22}
                                        variant="animated"
                                    />
                                }
                                label={
                                    <Typography variant="body2" fontWeight="bold" sx={{ml: '-4px', mb: '1px'}}>
                                        {getUserMembershipLabel(membership)}
                                    </Typography>
                                }
                                sx={{
                                    mr: 2,
                                    pl: "8px",
                                    border: "2px solid",
                                    borderColor: membership === "Ambassador" ? "#D4AF37" : "#A9A9A9",
                                    backgroundColor: membership === "Ambassador" 
                                        ? "rgba(212, 175, 55, 0.15)" // faint gold
                                        : "rgba(169, 169, 169, 0.15)", // faint silver
                                    color: membership === "Ambassador" ? "#D4AF37" : "#A9A9A9",
                                    boxShadow: `0 0 8px ${
                                        membership === "Ambassador" ? "rgba(212,175,55,0.6)" : "rgba(169,169,169,0.6)"
                                    }`,
                                    borderRadius: "12px",
                                    "& .MuiChip-icon": {
                                        marginLeft: "4px",
                                        color: membership === "Ambassador" ? "#D4AF37" : "#A9A9A9"
                                    },
                                    cursor: user.membership === "Free" ? 'pointer' : 'default',
                                    "&:hover": {
                                        backgroundColor:
                                            membership === "Ambassador"
                                            ? "rgba(212, 175, 55, 0.15)" 
                                            : "rgba(169, 169, 169, 0.15)"
                                    }
                                }}
                            />
                        )}

                        <Chip
                            label={
                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                    <Typography variant="body2" fontWeight="bold" sx={{mr: 1}}>
                                        Social Media:
                                    </Typography>
                                    <UserSocialMediaLinks ig={instagramHandle} links={socialMediaLinks} />    
                                </Box>
                            }
                            sx={{
                                border: membership === "Free" ? '' : '1px solid #ccc',
                                borderRadius: "12px",
                                backgroundColor: 'transparent'
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '170px',
                            border: '1px solid #D3D3D3',
                            borderRadius: 2,
                            p: 1.5,
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'}}>

                            <Box
                                component="img"
                                src={vehicleBackgroundImage}
                                alt="stat background"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    zIndex: 0,
                                    borderRadius: 2,
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: "rgba(255, 255, 255, 0.4)",
                                    zIndex: 1,
                                }}
                            /> 
                                
                            <Box sx={{
                                position: 'absolute', 
                                bottom: 0,
                                pb: 0.5, 
                                width: '100%', 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                maxHeight: '100%'}}>

                                <SVGIcon icon={getTransportMode(transportMode).icon} size={110} sx={{zIndex: 2, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.25))', mb: 1}} />

                            </Box>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '170px',
                            border: '1px solid #D3D3D3',
                            borderRadius: 2,
                            p: 1.5,
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'}}>

                            <Box
                                component="img"
                                src={vehicleImageUrl || globeBackgroundImage}
                                alt="stat background"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    zIndex: 0,
                                    borderRadius: 2,
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Trips */}
                    <Box sx={{ pb: 4, mt: 3 }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                            Trips Tracked:
                        </Typography>

                        <UserTripList trips={trips} userId={id} />
                    </Box>

                </Box>

            </Box>
        </Dialog>

        {displayMembershipsModal && (
            <MembershipsModal
                open={displayMembershipsModal}
                onClose={() => setDisplayMembershipsModal(false)}
            />
        )}
    </>);
}
